import { apiClient } from "./client";
import { PaginationMeta } from "../lib/bufferedPagination";

export type GetActivitiesParams = {
  ownerType?: "Admin";
  entityId?: string;
  q?: string;
  status?: "success" | "failed";
  sort?: "recent" | "oldest";
  page?: number;
  limit?: number;
};

export type ExportActivitiesParams = {
  status?: "Success" | "Failed";
};

export type ActivityRecord = {
  id: string;
  description: string;
  memberName: string;
  memberProfileImage?: string;
  action: string;
  module: string;
  target: string;
  targetId?: string;
  status: boolean;
  location: string;
  timestamp: string;
};

export type ActivitiesResult = {
  items: ActivityRecord[];
  meta?: PaginationMeta;
};

type AnyObject = Record<string, unknown>;

const asObject = (value: unknown): AnyObject | null => {
  if (typeof value === "object" && value !== null) return value as AnyObject;
  return null;
};

const asString = (value: unknown) => {
  if (typeof value === "string") return value;
  if (typeof value === "number") return String(value);
  return "";
};

const asBoolean = (value: unknown) => {
  if (typeof value === "boolean") return value;
  if (typeof value === "string") {
    const normalizedValue = value.toLowerCase();
    return normalizedValue === "true" || normalizedValue === "success";
  }
  if (typeof value === "number") return value === 1;
  return false;
};

const getOwnerName = (record: AnyObject) => {
  const owner = asObject(record.owner);
  if (owner) {
    const firstName = asString(owner.firstName).trim();
    const lastName = asString(owner.lastName).trim();
    const fullName = [firstName, lastName].filter(Boolean).join(" ").trim();
    if (fullName) return fullName;
    const ownerName = asString(owner.name).trim();
    if (ownerName) return ownerName;
    const ownerEmail = asString(owner.email).trim();
    if (ownerEmail) return ownerEmail;
  }

  return (
    asString(record.ownerName).trim() ||
    asString(record.actorName).trim() ||
    "Unknown Admin"
  );
};

const getAction = (record: AnyObject) => {
  return (
    asString(record.action).trim() ||
    asString(record.activity).trim() ||
    asString(record.event).trim() ||
    "Activity"
  );
};

const getOwnerProfileImage = (record: AnyObject) => {
  const owner = asObject(record.owner);
  const ownerProfileImage = owner ? asObject(owner.profileImage) : null;
  const profileImage = asObject(record.profileImage);

  return (
    (ownerProfileImage && asString(ownerProfileImage.url).trim()) ||
    (owner && asString(owner.profileImage).trim()) ||
    (profileImage && asString(profileImage.url).trim()) ||
    asString(record.profileImage).trim() ||
    ""
  );
};

const getModule = (record: AnyObject) => {
  return (
    asString(record.module).trim() ||
    asString(record.entityType).trim() ||
    asString(record.sourceType).trim() ||
    "Activity"
  );
};

const getTarget = (record: AnyObject) => {
  const entityIds = Array.isArray(record.entityIds) ? record.entityIds : [];
  const firstEntity = asObject(entityIds[0]);
  const entityTitle = firstEntity ? asString(firstEntity.title).trim() : "";

  if (entityTitle) {
    return entityTitle;
  }

  const target = asObject(record.target);
  const targetName =
    (target && asString(target.name).trim()) ||
    (target && asString(target.email).trim()) ||
    "";

  return (
    targetName ||
    asString(record.targetName).trim() ||
    asString(record.targetType).trim() ||
    asString(record.targetId).trim() ||
    "-"
  );
};

const getTargetId = (record: AnyObject) => {
  const entityIds = Array.isArray(record.entityIds) ? record.entityIds : [];
  const firstEntity = asObject(entityIds[0]);

  return (
    (firstEntity && asString(firstEntity._id).trim()) ||
    (firstEntity && asString(firstEntity.id).trim()) ||
    asString(record.targetId).trim() ||
    ""
  );
};

const getLocation = (record: AnyObject) => {
  return (
    asString(record.location).trim() ||
    asString(record.ipLocation).trim() ||
    asString(record.country).trim() ||
    "-"
  );
};

const getTimestamp = (record: AnyObject) => {
  return (
    asString(record.createdAt).trim() ||
    asString(record.timestamp).trim() ||
    new Date().toISOString()
  );
};

const normalizeActivity = (item: unknown, index: number): ActivityRecord => {
  const record = asObject(item) ?? {};
  const id =
    asString(record._id).trim() ||
    asString(record.id).trim() ||
    `activity-${index}`;

  return {
    id,
    description: asString(record.description).trim(),
    memberName: getOwnerName(record),
    memberProfileImage: getOwnerProfileImage(record) || undefined,
    action: getAction(record),
    module: getModule(record),
    target: getTarget(record),
    targetId: getTargetId(record) || undefined,
    status: asBoolean(record.status),
    location: getLocation(record),
    timestamp: getTimestamp(record),
  };
};

const getMeta = (payload: unknown): PaginationMeta | undefined => {
  const root = asObject(payload);
  if (!root) return undefined;

  const nestedData = asObject(root.data);
  const meta = asObject(root.meta) ?? (nestedData ? asObject(nestedData.meta) : null);

  if (!meta) return undefined;

  return {
    currentPage: typeof meta.currentPage === "number" ? meta.currentPage : undefined,
    totalPages: typeof meta.totalPages === "number" ? meta.totalPages : undefined,
    hasNext: typeof meta.hasNext === "boolean" ? meta.hasNext : undefined,
    hasPrevious: typeof meta.hasPrevious === "boolean" ? meta.hasPrevious : undefined,
    total: typeof meta.total === "number" ? meta.total : undefined,
    totalCount: typeof meta.totalCount === "number" ? meta.totalCount : undefined,
    count: typeof meta.count === "number" ? meta.count : undefined,
    limit: typeof meta.limit === "number" ? meta.limit : undefined,
    itemsPerPage:
      typeof meta.itemsPerPage === "number" ? meta.itemsPerPage : undefined,
  };
};

const normalizeActivities = (payload: unknown): ActivitiesResult => {
  if (Array.isArray(payload)) {
    return {
      items: payload.map((item, index) => normalizeActivity(item, index)),
    };
  }

  const root = asObject(payload);
  if (!root) return { items: [] };

  const candidateLists = [
    root.data,
    root.activities,
    root.items,
    root.results,
    asObject(root.data)?.activities,
    asObject(root.data)?.items,
    asObject(root.data)?.results,
  ];

  const list = candidateLists.find((value) => Array.isArray(value));
  if (!Array.isArray(list)) return { items: [], meta: getMeta(payload) };

  return {
    items: list.map((item, index) => normalizeActivity(item, index)),
    meta: getMeta(payload),
  };
};

export const getActivities = async (params: GetActivitiesParams) => {
  const response = await apiClient.get("/v1/activity/all", { params });
  return normalizeActivities(response.data);
};

const getFilenameFromDisposition = (disposition?: string) => {
  if (!disposition) return null;

  const utf8Match = disposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) return decodeURIComponent(utf8Match[1]);

  const plainMatch = disposition.match(/filename="?([^";]+)"?/i);
  if (plainMatch?.[1]) return plainMatch[1];

  return null;
};

export const exportActivities = async (params?: ExportActivitiesParams) => {
  const response = await apiClient.get("/v1/activity/admin/export/all", {
    params,
    responseType: "blob",
  });

  return {
    blob: response.data as Blob,
    filename:
      getFilenameFromDisposition(response.headers["content-disposition"]) ??
      "activity-log.csv",
  };
};
